import React from "react";
import {connect} from "react-redux";

import {useFacilitySettingsQuery} from "../page-admin-facility-settings/hooks/useFacilitySettingsQuery";
import {usePointClickCareStatus} from "../../hooks/usePointClickCareStatus";
import {useTemplateVersionQuery} from "../../hooks/useTemplateVersionQuery";
import {usePreflightTemplateQuery} from "../../hooks/useTemplateQuery";
import {Facilities} from "../../models/types/AdmissionForm";
import {AdminLayout} from "../../atoms/admin-layout";
import {AppState, select} from "../../store/store";
import {PreflightLayout} from "./PreflightLayout";
import {
  usePreflightResidentIdParam,
  usePreflightRouteParams,
  usePreflightWorkflowParam,
} from "./route";
import {usePreflightResidentPrefill} from "./hooks/usePreflightResidentPrefill";
import {Spinner} from "../../atoms/spinner";
import {transformV3FileUrl} from "../../utils/url";
import {getCommonAuthorizationHeader} from "../../services/api/selectors";

export const PageAdminPreflightRenderer = ({
  facilities,
}: {
  facilities: Facilities;
}) => {
  const {admissionId, facilityId} = usePreflightRouteParams();
  const workflow = usePreflightWorkflowParam();
  const residentId = usePreflightResidentIdParam();
  const isNewPreflight = !admissionId;

  const {isFetching: isFetchingFacilitySettings, data: facilitySettings} =
    useFacilitySettingsQuery({facilityId});

  const {data: {version, admission} = {}, isFetching: isFetchingVersion} =
    useTemplateVersionQuery({
      admissionId,
      facilityId,
    });

  const {
    data: template,
    isFetching: isFetchingTemplate,
  } = usePreflightTemplateQuery({
    versionId: version?.id,
    workflowType: admission?.workflowType || workflow,
  });

  const {data: residentPrefillData, isFetching: isFetchingResidentPrefill} =
    usePreflightResidentPrefill({
      residentId,
      enabled: isNewPreflight && Boolean(residentId),
    });

  const residentPrefill = residentPrefillData?.prefill;
  const presetLinkedResident = residentPrefillData?.resident;

  const {isPccEnabled} = usePointClickCareStatus(facilities, facilityId);

  const isLoading =
    isFetchingFacilitySettings ||
    isFetchingVersion ||
    isFetchingTemplate ||
    isFetchingResidentPrefill;

  return (
    <AdminLayout>
      {isLoading ? (
        <Spinner />
      ) : (
        <PreflightLayout
          facilityId={facilityId}
          admission={admission}
          version={version}
          residentPrefill={residentPrefill}
          presetLinkedResident={presetLinkedResident}
          facilitySettings={{
            ...facilitySettings,
            transformFileUrl: transformV3FileUrl,
            getCommonAuthorizationHeader,
          }}
          admissionId={admissionId}
          template={template}
          isPointClickCareEnabled={isPccEnabled}
        />
      )}
    </AdminLayout>
  );
};

const mapStateToProps = (state: AppState) => ({
  facilities: select.adminSession.facilities(state),
});

export const PageAdminPreflight = connect(mapStateToProps)(
  PageAdminPreflightRenderer,
);
